import { useEffect, useState } from "react";
import { getPopularDestinationsFromOffers } from "@/adapters/popularDestinationsAdapter";
import PopularDestinationsSkeleton from "./PopularDestinationsSkeleton";
import { getMostSearched } from "@/services/offers.service";
import { Offer } from "@/types/offer";

const FALLBACK_IMAGES = [
  "https://s3.amazonaws.com/media.costamar.com/IMG-WEB/DEFAULT04.jpg",
  "https://s3.amazonaws.com/media.costamar.com/IMG-WEB/DEFAULT06.jpg",
];

const getRandomFallbackImage = () =>
  FALLBACK_IMAGES[Math.floor(Math.random() * FALLBACK_IMAGES.length)];

interface DestinationCardProps {
  destination: { rank: number; city: string; image: string; url: string };
  index: number;
}

const DestinationCard = ({ destination, index }: DestinationCardProps) => {
  const [imgSrc, setImgSrc] = useState(destination.image);

  return (
    <a
      href={destination.url}
      target="_blank"
      rel="noopener noreferrer"
      className="relative group rounded-2xl overflow-hidden aspect-[3/4] animate-fade-in"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <img
        src={imgSrc}
        alt={destination.city}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        onError={() => setImgSrc(getRandomFallbackImage())}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
      <div className="absolute bottom-4 left-4 right-4">
        <p className="text-primary-foreground font-bold text-lg">
          #{destination.rank} {destination.city}
        </p>
      </div>
    </a>
  );
};

const PopularDestinations = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchSpecialOffers = async () => {
      setIsLoading(true);
      try {
        const data = await getMostSearched({
          limit: 4,
        });
        setOffers(data);
      } catch (err: any) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpecialOffers();
  }, []);

  if (error) {
    return null;
  }

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl lg:text-4xl font-light text-foreground italic mb-10">
          Destinos más buscados
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <PopularDestinationsSkeleton key={index} />
              ))
            : offers.map((offer, index) => (
                <DestinationCard
                  key={index}
                  destination={getPopularDestinationsFromOffers(offer, index)}
                  index={index}
                />
              ))}
        </div>

        {error && <p className="text-red-500">{error}</p>}
      </div>
    </section>
  );
};

export default PopularDestinations;